/**
 * Instant artwork builder — step controller.
 *
 * Owns step order/visibility and the step tracker. Individual steps
 * own their own selection/validation logic and just toggle their own
 * [data-continue] button's disabled state; this controller only reacts
 * to clicks on that button to move forward, and keeps the photos
 * (passed up via a custom event) for the design step to read.
 */
(function () {
  function init() {
    const root = document.querySelector('[data-instant-artwork-builder]');
    if (!root) return;

    const steps = Array.from(root.querySelectorAll('[data-step]'));
    if (!steps.length) return;

    const totalSteps = parseInt(root.dataset.totalSteps, 10) || steps.length;
    let currentIndex = 0;

    // Shared state, read by the design step (it needs the compressed photos).
    const state = {
      photos: [],
    };

    function showStep(index) {
      steps.forEach((step, i) => {
        step.hidden = i !== index;
      });
      const label = steps[index].querySelector('[data-step-label]');
      if (label) renderProgress(label, index);
      // Page-level hook so CSS outside the builder can react to the step
      // (e.g. mobile hides the product gallery while designing).
      document.documentElement.dataset.instantArtworkStep = steps[index].dataset.step;
      steps[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
      steps[index].dispatchEvent(new CustomEvent('instant-artwork:step-shown', { bubbles: true }));
    }

    // Line icons per step, the same ones the other NavaBird product builders
    // use (snippets/nb-step-icon.liquid): frame, upload cloud, name tag, palette.
    const SVG_OPEN =
      '<svg class="instant-artwork__progress-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">';
    const STEP_ICONS = {
      format:
        '<rect x="4" y="3" width="12" height="15" rx="1.5"/><path d="M7 14l2.5-3 2 2.2L13 11.5l2 2.5"/><path d="M19.5 7v14M17.8 7h3.4M17.8 21h3.4M4 21h11.5M4 19.5v3M15.5 19.5v3"/>',
      upload: '<path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 8.5a4 4 0 0 1-.5 7.97"/><path d="M12 12v8M8.8 15.2 12 12l3.2 3.2"/>',
      name: '<path d="M3.5 12.6V5a1.5 1.5 0 0 1 1.5-1.5h7.6l8 8a1.5 1.5 0 0 1 0 2.1l-6.4 6.4a1.5 1.5 0 0 1-2.1 0z"/><circle cx="8.2" cy="8.2" r="1.4" fill="currentColor"/>',
      review:
        '<path d="M12 3a9 9 0 1 0 0 18c1.1 0 1.8-.8 1.8-1.8 0-.5-.2-.9-.5-1.2-.3-.3-.5-.7-.5-1.2 0-1 .8-1.8 1.8-1.8h2.1A4.3 4.3 0 0 0 21 10.7C21 6.4 17 3 12 3z"/><circle cx="7.5" cy="11" r="1.2" fill="currentColor"/><circle cx="10" cy="7.3" r="1.2" fill="currentColor"/><circle cx="14.5" cy="7.3" r="1.2" fill="currentColor"/>',
    };
    const CHECK_BADGE =
      '<span class="instant-artwork__progress-check"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>';

    // Spans only — the label element is a <p>, which can't hold lists.
    function renderProgress(label, index) {
      label.classList.add('instant-artwork__progress');
      label.setAttribute('aria-label', `Step ${index + 1} of ${totalSteps}`);
      label.textContent = '';
      for (let i = 0; i < totalSteps; i++) {
        const item = document.createElement('span');
        item.className = 'instant-artwork__progress-step';
        if (i < index) item.classList.add('is-done');
        if (i === index) item.classList.add('is-current');
        item.setAttribute('aria-hidden', 'true');

        const dot = document.createElement('span');
        dot.className = 'instant-artwork__progress-dot';
        const icon = steps[i] && STEP_ICONS[steps[i].dataset.step];
        dot.innerHTML = (icon ? `${SVG_OPEN}${icon}</svg>` : String(i + 1)) + CHECK_BADGE;
        const name = document.createElement('span');
        name.className = 'instant-artwork__progress-name';
        name.textContent = (steps[i] && steps[i].dataset.stepTitle) || `Step ${i + 1}`;

        item.append(dot, name);
        label.appendChild(item);
      }
    }

    function goNext() {
      if (currentIndex < steps.length - 1) {
        currentIndex += 1;
        showStep(currentIndex);
      }
    }

    function goBack() {
      if (currentIndex > 0) {
        currentIndex -= 1;
        showStep(currentIndex);
      }
    }


    root.addEventListener('click', (event) => {
      const continueBtn = event.target.closest('[data-continue]');
      if (continueBtn && !continueBtn.disabled) {
        goNext();
        return;
      }
      const backBtn = event.target.closest('[data-back]');
      if (backBtn) {
        goBack();
      }
    });

    root.addEventListener('instant-artwork:photos-ready', (event) => {
      state.photos = event.detail.photos;
    });

    // Exposed so the design step (a separate script) can read the photos.
    window.__instantArtworkState = state;

    showStep(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
